import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from 'src/constants';
import { useQueryClient } from '@tanstack/react-query';
import logger from 'src/utils/logger';
import { invalidateCaseQueries } from './case';
import { handleUnauthorized } from './authToken';

export interface ChatAttachment {
  id: number;
  filename: string;
  mimetype: string;
}
export interface CommentBody {
  message: string | null;
  attachments?: ChatAttachment[];
}

export interface CaseBody {
  case_id: number | null;
  status: string;
  message: string;
  attachments?: ChatAttachment[];
}

export type ChatKind = 'message' | 'note' | 'status_change' | 'system' | 'created';

export interface TrackingChange {
  field: string;
  old: string | null;
  new: string | null;
}

export interface ChatItem {
  id: number;
  parent_id: number | null;
  user_from: string | null;
  timestamp: string;
  message_type: string | null;
  kind?: ChatKind;
  body: CommentBody | CaseBody;
  tracking?: TrackingChange[] | null;
  avatar: string | null;
  is_case: boolean | null;
  is_note: boolean | null;
  is_external: boolean | null;
  is_own?: boolean | null;
  category: string | null;
  is_deletable: boolean | null;
  is_editable: boolean | null;
}

export interface MentionCandidate {
  id: number;
  name: string;
  email: string;
}

export interface EntityCandidate {
  model: string;
  id: number;
  name: string;
  reference: string;
  type_label: string;
  deep_link: string;
}

const sortChatMessages = (messages: ChatItem[]): ChatItem[] => {
  return messages.sort((a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf());
};

const useChat = (model: string, res_id: number) => {
  const accessToken = localStorage.getItem('access_token');
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatItem[]>([]);
  const [allMessagesFetched, setAllMessagesFetched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const messageUpdating = useRef(false);
  const queryClient = useQueryClient();

  const PAGE_SIZE = 50;

  const fetchMessages = async (pageToFetch: number, append: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}portal/utility/chatter/chat/${model}/${res_id}?page=${pageToFetch}&size=${PAGE_SIZE}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to fetch chat messages: ${response.statusText}`);
      }

      const newMessages = await response.json();
      if (!newMessages || !newMessages.items || newMessages.items.length < PAGE_SIZE) {
        setAllMessagesFetched(true);
      }
      if (append) {
        setMessages(prev => sortChatMessages([...prev, ...newMessages.items]));
      } else {
        setMessages(sortChatMessages(newMessages.items));
      }
    } catch (err) {
      logger.error('Failed to fetch chat messages', err);
      setError(t('errors.fetch_messages_failed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreMessages = () => {
    if (allMessagesFetched) return;
    const newPage = page + 1;
    setPage(newPage);
    fetchMessages(newPage, true);
  };

  useEffect(() => {
    setMessages([]);
    setPage(1);
    setAllMessagesFetched(false);
    setError(null);
    fetchMessages(1, false);
  }, [model, res_id]);

  const searchMentionable = async (q: string): Promise<MentionCandidate[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}portal/utility/chatter/mentionable?q=${encodeURIComponent(q)}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        return [];
      }
      return (await response.json()) as MentionCandidate[];
    } catch (err) {
      logger.error('Failed to search mentionable users', err);
      return [];
    }
  };

  const searchLinkable = async (q: string, type?: string): Promise<EntityCandidate[]> => {
    try {
      const typeParam = type ? `&type=${encodeURIComponent(type)}` : '';
      const response = await fetch(
        `${API_BASE_URL}portal/utility/chatter/linkable?q=${encodeURIComponent(q)}${typeParam}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        return [];
      }
      return (await response.json()) as EntityCandidate[];
    } catch (err) {
      logger.error('Failed to search linkable entities', err);
      return [];
    }
  };

  const createMessage = async (
    message: string,
    attachments?: { file: string; name: string; mimetype: string }[],
    createCase?: boolean,
    isNote?: boolean,
    mentionedPartnerIds?: number[],
  ): Promise<boolean> => {
    if (!message && (!attachments || !attachments.length)) return false;
    logger.info('Creating chat message');
    setLoading(true);
    setError(null);

    const chatBody: Record<string, unknown> = {
      attachments,
      message,
      model,
      res_id,
      is_case: createCase || false,
      is_note: isNote || false,
    };

    if (mentionedPartnerIds && mentionedPartnerIds.length) {
      chatBody.mentioned_partner_ids = mentionedPartnerIds;
    }

    try {
      const response = await fetch(`${API_BASE_URL}portal/utility/chatter/chat`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(chatBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to create chat message: ${response.statusText}`);
      }
      invalidateCaseQueries(queryClient);

      const newMessage = await response.json();
      setMessages(prev => [newMessage, ...prev]);
      return true;
    } catch (err) {
      logger.error('Failed to create chat message', err);
      setError(t('errors.create_message_failed'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateMessage = async (messageId: number, message: string): Promise<boolean> => {
    if (messageUpdating.current) return false;
    logger.info('Updating chat message', messageId);
    messageUpdating.current = true;
    const updatedBody = {
      message,
    };

    try {
      const response = await fetch(`${API_BASE_URL}portal/utility/chatter/chat/${messageId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify(updatedBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to update chat message: ${response.statusText}`);
      }

      const newMessage = await response.json();
      const updatedMessages: ChatItem[] = messages.map(message => {
        if (message.id === newMessage.id) {
          return newMessage;
        }
        return message;
      });
      setMessages(updatedMessages);
      return true;
    } catch (err) {
      logger.error('Failed to update chat message', err);
      setError(t('errors.update_message_failed'));
      return false;
    } finally {
      messageUpdating.current = false;
    }
  };

  const createCaseFromMessage = async (messageId: number) => {
    if (messageUpdating.current) return false;
    logger.info('Creating case from message', messageId);
    messageUpdating.current = true;
    const updatedBody = {
      is_case: true,
    };

    try {
      const response = await fetch(`${API_BASE_URL}portal/utility/chatter/chat/${messageId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body: JSON.stringify(updatedBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to create case from message: ${response.statusText}`);
      }

      const newMessage = await response.json();
      const updatedMessages: ChatItem[] = messages.map(message => {
        if (message.id === newMessage.id) {
          return newMessage;
        }
        return message;
      });

      setMessages(updatedMessages);
      return true;
    } catch (err) {
      logger.error('Failed to create case from message', err);
      setError(t('errors.create_case_from_message'));
      return false;
    } finally {
      messageUpdating.current = false;
    }
  };

  const deleteMessage = async (messageId: number): Promise<boolean> => {
    logger.info('Deleting chat message', messageId);
    try {
      const response = await fetch(`${API_BASE_URL}portal/utility/chatter/chat/${messageId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(accessToken);
        }
        throw new Error(`Failed to delete chat message: ${response.statusText}`);
      }
      const remainingMessages = messages.filter(message => message.id !== messageId);
      setMessages(remainingMessages);
      return true;
    } catch (err) {
      logger.error('Failed to delete chat message', err);
      setError(t('errors.delete_message_failed'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    messages,
    loading,
    error,
    createMessage,
    updateMessage,
    deleteMessage,
    fetchMoreMessages,
    allMessagesFetched,
    createCaseFromMessage,
    searchMentionable,
    searchLinkable,
  };
};

export default useChat;
